import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    Alert,
    CircularProgress,
    Stack,
    Divider
} from '@mui/material';
import {
    Save as SaveIcon,
    Refresh as RefreshIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import apiService from '../services/api.service';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../store/slices/uiSlice';

interface SystemConfig {
    [key: string]: {
        value: string;
        description: string;
        lastModified: string;
    };
}

const GeneralSettings: React.FC = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState<SystemConfig>({});
    const [editedValues, setEditedValues] = useState<Record<string, string>>({});

    const loadConfig = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiService.getSystemConfig();
            const data = response?.data || {};
            setConfig(data);

            const values: Record<string, string> = {};
            Object.keys(data).forEach(key => {
                values[key] = data[key].value;
            });
            setEditedValues(values);
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error.message || 'Failed to load system configuration',
                severity: 'error'
            }));
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const updates = Object.keys(editedValues).map(key => ({
                key,
                value: editedValues[key]
            }));

            await apiService.updateSystemConfig(updates);
            dispatch(showSnackbar({
                message: 'System configuration updated successfully',
                severity: 'success'
            }));
            loadConfig();
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error.message || 'Failed to update system configuration',
                severity: 'error'
            }));
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (key: string, value: string) => {
        setEditedValues(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleReset = () => {
        const values: Record<string, string> = {};
        Object.keys(config).forEach(key => {
            values[key] = config[key].value;
        });
        setEditedValues(values);
        dispatch(showSnackbar({
            message: 'Changes reset',
            severity: 'info'
        }));
    };

    const configFields = [
        {
            key: 'FISCAL_YEAR',
            label: 'Fiscal Year',
            type: 'number',
            helpText: 'Current fiscal year for the system'
        },
        {
            key: 'PR_NUMBER_FORMAT',
            label: 'PR Number Format',
            type: 'text',
            helpText: 'Format for PR number generation (e.g., PR-YYYY-MM-####)'
        },
        {
            key: 'DEFAULT_CURRENCY',
            label: 'Default Currency',
            type: 'text',
            helpText: 'Default currency code (e.g., SAR, USD)'
        },
        {
            key: 'MAX_FILE_SIZE',
            label: 'Max File Size (bytes)',
            type: 'number',
            helpText: 'Maximum file upload size in bytes'
        },
        {
            key: 'SESSION_TIMEOUT',
            label: 'Session Timeout (minutes)',
            type: 'number',
            helpText: 'Session timeout duration in minutes'
        },
        {
            key: 'EMAIL_ENABLED',
            label: 'Email Notifications',
            type: 'select',
            options: ['true', 'false'],
            helpText: 'Enable or disable email notifications'
        },
        {
            key: 'BUDGET_CHECK_ENABLED',
            label: 'Budget Check',
            type: 'select',
            options: ['true', 'false'],
            helpText: 'Enable or disable budget checking'
        },
        {
            key: 'OPERATIONS_MANAGER_EMAIL',
            label: 'Operations Manager Email',
            type: 'email',
            helpText: 'Email address for Operations Manager'
        },
        {
            key: 'FINANCE_MANAGER_EMAIL',
            label: 'Finance Manager Email',
            type: 'email',
            helpText: 'Email address for Finance Manager'
        },
        {
            key: 'GENERAL_MANAGER_EMAIL',
            label: 'General Manager Email',
            type: 'email',
            helpText: 'Email address for General Manager'
        },
        {
            key: 'COMPANY_NAME',
            label: 'Company Name',
            type: 'text',
            helpText: 'Company name for email templates and documents'
        }
    ];

    return (
        <Box component="section" aria-labelledby="gs-heading" sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={{ xs: 1.5, sm: 0 }}
                    mb={3}
                >
                    <Typography
                        id="gs-heading"
                        variant="h5"
                        component="h1"
                        sx={{ fontSize: { xs: 18, sm: 22, md: 24 }, fontWeight: 700 }}
                    >
                        <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} aria-hidden="true" />
                        General Settings
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleReset}
                            disabled={loading || saving}
                        >
                            Reset
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                            onClick={handleSave}
                            disabled={loading || saving}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Stack>
                </Stack>

                <Alert severity="info" sx={{ mb: 3 }}>
                    Configure system-wide settings including fiscal year, PR number format, and notification preferences.
                </Alert>

                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {configFields.map((field) => (
                            <Grid item xs={12} md={6} key={field.key}>
                                <Stack spacing={0.5}>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        {field.label}
                                    </Typography>
                                    {field.type === 'select' ? (
                                        <select
                                            value={editedValues[field.key] || ''}
                                            onChange={(e) => handleChange(field.key, e.target.value)}
                                            style={{
                                                padding: '10px',
                                                borderRadius: '4px',
                                                border: '1px solid #ccc',
                                                fontSize: '14px'
                                            }}
                                        >
                                            {field.options?.map(option => (
                                                <option key={option} value={option}>{option}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            type={field.type}
                                            value={editedValues[field.key] || ''}
                                            onChange={(e) => handleChange(field.key, e.target.value)}
                                            size="small"
                                            placeholder={config[field.key]?.description || ''}
                                        />
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        {field.helpText}
                                    </Typography>
                                    {config[field.key]?.lastModified && (
                                        <Typography variant="caption" color="text.secondary" fontStyle="italic">
                                            Last modified: {new Date(config[field.key].lastModified).toLocaleString()}
                                        </Typography>
                                    )}
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Divider sx={{ my: 3 }} />

                <Alert severity="warning">
                    <strong>Important:</strong> Changes to these settings will affect the entire system.
                    Please ensure you understand the impact before saving changes.
                </Alert>
            </Paper>
        </Box>
    );
};

export default GeneralSettings;
